import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, ArrowUpRight, Sparkles } from 'lucide-react';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import type { MonthlyReviewRosterRow, MonthlyReviewStatus, ProgressTimelineItem } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';
import { formatMonthKey, weekdayLabel } from '../../../lib/sessionTime';
import { SelectionBar } from '../../../components/SelectionBar';
import { useAiDraft, useSaveMonthlyReview, useStudentMonthNotes } from '../../../hooks/useProgress';
import { useSubscription } from '../../../hooks/useSubscription';
import { AiDraftButton, type AiDraftState } from './AiDraftButton';

/** The month AFTER `month` — what the editor hint promises the parent reads.
 *  Formats through the same `formatMonthKey` every other month display in
 *  this file uses, so a bare "M/YYYY" is the one shape this whole panel ever
 *  shows (fix wave C1/F3/I2). */
function nextMonthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 1));
  const nextKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  return formatMonthKey(nextKey);
}

/** Imperative surface for a caller (MonthlyReviewsPage) that wants to
 *  navigate AWAY from this student — pick another roster row, or leave the
 *  page entirely — without silently discarding unsaved text. */
export interface ReviewEditorHandle {
  /** Runs `proceed` immediately if there's nothing that would be lost;
   *  otherwise opens the same "Bỏ nội dung vừa viết?" confirm the internal
   *  "Bỏ qua" button uses, and only runs `proceed` if the teacher discards. */
  guardLeave: (proceed: () => void) => void;
}

interface ReviewEditorPanelProps {
  row: MonthlyReviewRosterRow;
  subjectId: string;
  subjectName: string;
  month: string;
  existingContent: string;
  /** THI-348: DRAFT = the loaded text is an unapproved (batch AI) draft: the
   *  panel says so and offers "Lưu nháp" / "Duyệt". Omitted = not a draft. */
  existingStatus?: MonthlyReviewStatus | null;
  /** The loaded review's own `aiDrafted`, so approving or re-saving a draft
   *  the batch wrote keeps it labelled as AI-drafted. */
  existingAiDrafted?: boolean;
  onSaved: () => void;
  onSaveAndNext: () => void;
  position: number;
  total: number;
  /** Phone-only "Bỏ qua": advance to the next student in the currently
   *  filtered list WITHOUT saving. Omitted → no skip button (desktop's own
   *  inline footer never renders one — handoff §2 shows it on phone only). */
  onSkip?: () => void;
  /** Phone-only: renders a back chevron before the student name and calls
   *  this through the same `guardLeave` dirty-check the roster picker uses.
   *  Omitted on desktop, where the page's own PageHeader already carries
   *  back navigation and this panel sits inside a two-column layout, not a
   *  full-screen swap (THI-189, spec §10.7 — one header on phone, never
   *  the page header stacked on top of this panel's own). */
  onBack?: () => void;
  /** 'single' is the student-page entry (D3): no position counter and no
   *  "Lưu và sang học viên tiếp", only Lưu and the AI button (spec §11.4). */
  mode?: 'sweep' | 'single';
  /** Lets the surrounding ResponsiveSheet guard a backdrop tap. The panel's
   *  own `guardLeave` still guards in-panel navigation; this only mirrors the
   *  same `dirty` value outward. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Keyed by `row.studentId` at the call site (MonthlyReviewsPage) so React
 * remounts this component per student instead of patching it in place — the
 * React-Compiler-clean way to "reset all state when an identity changes"
 * (an effect that calls setState synchronously is the antipattern that
 * pattern replaces, and it's now a lint error here — see
 * https://react.dev/learn/you-might-not-need-an-effect). The remount is why
 * the call site must not render this until `existingContent` has actually
 * resolved (see the `existing.isPending` branch there): `useState`'s
 * initializer only runs once per mount, so a stale '' would never self-heal
 * if the async fetch were still in flight when this first mounted.
 */

/** THI-193: the GHI CHÚ TRONG THÁNG column prints sectioned notes as
 *  "Tên mục: nội dung" lines, then the free text (handoff 19 §6). The
 *  column is a reading aid for the review, not the editor, so plain
 *  lines are enough. */
function itemLines(items: { name: string; content: string }[], free: string | null): string {
  return [...items.map((i) => `${i.name}: ${i.content}`), free ?? ''].filter(Boolean).join('\n');
}
function myText(i: { myItems: { name: string; content: string }[]; myComment: string | null }) {
  return itemLines(i.myItems, i.myComment);
}
function classText(i: { classItems: { name: string; content: string }[]; classComment: string | null }) {
  return itemLines(i.classItems, i.classComment);
}

export const ReviewEditorPanel = forwardRef<ReviewEditorHandle, ReviewEditorPanelProps>(function ReviewEditorPanel(
  {
    row, subjectId, subjectName, month, existingContent, existingStatus = null, existingAiDrafted = false,
    onSaved, onSaveAndNext, position, total,
    onSkip, onBack, mode = 'sweep', onDirtyChange,
  },
  ref,
) {
  const { t } = useTranslation('progress');
  const { t: tCommon } = useTranslation('common');
  const isMobile = useIsMobile();
  const [content, setContent] = useState(existingContent);
  // 'ai' the moment a draft lands, and it STAYS 'ai' through editing — the
  // banner and the saved `aiDrafted` flag both key on it. Only "Xoá, tự viết"
  // clears it (port guide §3.3).
  const [draftSource, setDraftSource] = useState<'manual' | 'ai'>('manual');
  const [draftCount, setDraftCount] = useState(0);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [quotaHit, setQuotaHit] = useState(false);
  // A navigation attempt (pick another row, leave via back, "Bỏ qua") that's
  // waiting on the discard-confirm below. Holds the deferred action itself,
  // not just a boolean, so confirming can run exactly the move that asked.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  // Same "would this navigation lose something" test the AI-replace check
  // above uses for content, plus the AI-drafted flag: a draft the teacher
  // never edited still isn't reflected in the saved review yet.
  const dirty = content !== existingContent || draftSource === 'ai';

  // Reported to whichever ancestor owns a surrounding ResponsiveSheet (D3's
  // single-student entry) so its OWN backdrop-tap/Escape/swipe-down guard
  // asks the same question `requestLeave` below already asks for in-panel
  // navigation. The caller must pass a stable setter (`setEditorDirty` from
  // `useState` is stable), not an inline arrow, or this effect loops.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const requestLeave = useCallback(
    (proceed: () => void) => {
      if (!dirty) { proceed(); return; }
      setPendingLeave(() => proceed);
    },
    [dirty],
  );

  // Exposed so the roster list and the page header (both OUTSIDE this
  // component's own tree) can route a navigation through the same guard the
  // in-editor "Bỏ qua" button uses below, instead of unmounting this
  // (keyed) instance out from under unsaved text.
  useImperativeHandle(ref, () => ({ guardLeave: requestLeave }), [requestLeave]);

  const notes = useStudentMonthNotes(row.studentId, subjectId, month);
  const save = useSaveMonthlyReview(row.studentId, subjectId, month);
  const draft = useAiDraft(row.studentId, subjectId, month);
  const displayName = row.nickname || row.studentName.trim().split(/\s+/).at(-1) || row.studentName;

  const runDraft = () => {
    draft.mutate(undefined, {
      onSuccess: (d) => {
        setContent(d.content);
        setDraftCount(d.sourceNoteCount);
        setDraftSource('ai');
      },
      // `fetchApi` attaches the error body's `code` onto the thrown Error
      // (lib/api.ts) — tell TIER_LIMIT_REACHED (402, quota) apart from every
      // other failure (NO_NOTES shouldn't reach here since the button is
      // disabled at 0 notes; AI_DRAFT_FAILED/network are both "still charged,
      // try again" per handoff §7.9) by that code, never by matching the
      // Vietnamese message text.
      onError: (err: Error & { code?: string }) => {
        if (err.code === 'TIER_LIMIT_REACHED') setQuotaHit(true);
        else toast.error(t('review.ai.failedToast'));
      },
    });
  };

  const onAiClick = () => (content.trim() ? setReplaceOpen(true) : runDraft());

  const aiState: AiDraftState = quotaHit ? 'quota' : row.noteCount === 0 ? 'noNotes' : 'ready';
  const monthItems = notes.data?.months[0]?.items ?? [];
  const sessionItems = monthItems.filter(
    (i): i is Extract<ProgressTimelineItem, { kind: 'SESSION' }> => i.kind === 'SESSION',
  );

  // "Credit AI còn N" — the same numbers the sidebar meter reads
  // (useSubscription().usage). -1 means unlimited and 0 means the plan has no
  // AI at all; in both cases the line is simply absent rather than lying —
  // and while the subscription query is still pending, `subscription` is
  // undefined, `limit` falls back to 0, and the line stays absent rather than
  // flashing a wrong "0 credits" (INVARIANTS §4).
  const { data: subscription } = useSubscription();
  const limit = subscription?.usage.aiCreditsLimit ?? 0;
  const creditsLine =
    limit > 0 ? t('review.credits', { count: Math.max(0, limit - subscription!.usage.aiCreditsUsed) }) : null;

  const isDraft = existingStatus === 'DRAFT';
  // `status` DRAFT only from "Lưu nháp" on a draft; every other save
  // publishes, as before. `aiDrafted` survives approval of a batch draft.
  const doSave = (then: () => void, status: MonthlyReviewStatus = 'PUBLISHED') =>
    save.mutate(
      { content: content.trim(), aiDrafted: draftSource === 'ai' || (isDraft && existingAiDrafted), status },
      {
        onSuccess: () => {
          toast.success(
            status === 'DRAFT'
              ? t('review.draftSavedToast', { name: displayName })
              : t('review.savedToast', { month: formatMonthKey(month), name: displayName }),
          );
          // `content` re-converges with `existingContent` once this save is
          // reflected, but `draftSource` stayed 'ai' forever without this —
          // the dirty guard (`content !== existingContent || draftSource ===
          // 'ai'`) would then keep firing "Bỏ nội dung vừa viết?" over a
          // review that IS saved (fix wave C3/I7).
          setDraftSource('manual');
          then();
        },
      },
    );

  return (
    <div
      className={cn(
        '@container flex flex-col overflow-hidden bg-surface-0',
        // Phone (onBack supplied): the editor IS the whole page (spec §11.3,
        // port guide §3.5) — full h-[100dvh], no card chrome. Sweep desktop
        // (no onBack, mode='sweep'): the existing bordered card, sized by
        // its grid cell. Single-student (mode='single', D3): no onBack
        // either, but this instance sits INSIDE a ResponsiveSheet that
        // already supplies the panel's outer chrome (rounded corners on the
        // Drawer, a plain edge on the Sheet) — repeating a border here would
        // draw a card-inside-a-card, so it just fills the remaining height
        // instead (`flex-1`; ResponsiveSheet's SheetContent/DrawerContent
        // are already `flex flex-col`).
        onBack
          ? 'h-[100dvh]'
          : mode === 'single'
            ? 'min-h-0 flex-1'
            : 'min-h-0 rounded-lg border border-surface-200',
      )}
    >
      {onBack ? (
        // D2 phone write frame (mock frame 3): back chevron · tên ·
        // position, replacing PageHeader entirely — ONE header on this
        // screen, never stacked with the page's own (port guide §3.5).
        <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-surface-200 bg-surface-0 px-5">
          <button
            type="button"
            onClick={() => requestLeave(onBack)}
            aria-label={tCommon('back')}
            className="-ml-2.5 flex h-10 w-10 shrink-0 items-center justify-center text-surface-600"
          >
            <ArrowLeft className="h-[18px] w-[18px]" strokeWidth={2} />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold tracking-[-0.01em] text-surface-900">
              {row.studentName}
              {row.nickname && <span className="font-normal text-surface-500"> ({row.nickname})</span>}
            </p>
            <p className="text-[12px] text-surface-500">
              {subjectName} · {t('progress.monthTitle', { month: formatMonthKey(month) })}
            </p>
          </div>
          <span className="shrink-0 tabular-nums text-[12.5px] text-surface-500">
            {t('review.position', { index: position, total })}
          </span>
        </header>
      ) : mode === 'single' ? (
        // No header block at all: the surrounding ResponsiveSheet already
        // renders one (its `title`/`description` props), and repeating
        // row.studentName + panelMeta here would be a second, redundant
        // header stacked under the first one — the student's name is
        // already the page's own big heading one level up.
        null
      ) : (
        <div className="flex shrink-0 items-center gap-4 border-b border-surface-100 px-6 pb-4 pt-5">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-surface-900">
              {row.studentName}
              {row.nickname && <span className="font-normal text-surface-500"> ({row.nickname})</span>}
            </h2>
            <p className="mt-0.5 text-[12.5px] text-surface-500">
              {t('review.panelMeta', {
                subject: subjectName, month: formatMonthKey(month),
                sessions: row.sessions, present: row.present, excused: row.excused, absent: row.absent,
              })}
            </p>
          </div>
          {mode === 'sweep' && (
            <span className="shrink-0 tabular-nums text-[12px] text-surface-400">
              {t('review.position', { index: position, total })}
            </span>
          )}
          {mode === 'sweep' && (
            <Link
              to={`/students/${row.studentId}?tab=tien-do`}
              className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-brand-600"
            >
              {t('review.profileLink')}
              <ArrowUpRight className="h-[13px] w-[13px]" strokeWidth={2} />
            </Link>
          )}
        </div>
      )}

      <div
        className={cn(
          // Container query, not viewport (`lg:`): the panel renders inside
          // BOTH a 480px Sheet (student page → Tiến độ → Sửa/Viết) and a
          // desktop sweep column that can itself be under 720px wide — the
          // split must react to the PANEL's own rendered width, not the
          // window's (THI-189, spec §10.7). 720px: the desktop sweep panel is
          // 768px wide and the Sheet is 480px, so the threshold must clear
          // the Sheet's width while still landing inside the sweep panel's.
          // `content-start`: Grid's default `align-content: stretch` was
          // distributing the flex-1 container's leftover vertical space
          // across BOTH auto-sized rows in single-column (phone) mode,
          // inflating the notes row well past its own content and opening a
          // large blank gap before the review section — fix round 1 finding
          // 1. Desktop's `@[720px]:grid-cols-2` has just one row (both
          // columns side by side), where stretch-to-fill IS wanted so each
          // column's own `overflow-y-auto` gets the full available height —
          // restore it there.
          'grid min-h-0 flex-1 content-start grid-cols-1 overflow-y-auto @[720px]:content-stretch @[720px]:grid-cols-2 @[720px]:overflow-hidden',
          // Room for the fixed phone footer (below) so the last line of the
          // hint text isn't hidden behind it once scrolled to the bottom.
          isMobile && 'pb-24',
        )}
      >
        {/* Source on the left, pen on the right: both in one field of view.
            Single mode stacks both columns in ONE scroll container (spec
            §11.4) — a bottom hairline here separates "GHI CHÚ TRONG THÁNG"
            from "NHẬN XÉT THÁNG M" below it, swapped for the desktop
            sweep's right-hand rule once the container is wide enough to
            actually be two columns side by side. */}
        <div className="min-w-0 overflow-y-auto border-b border-surface-100 px-6 py-4 @[720px]:border-b-0 @[720px]:border-r">
          <div className="mb-2.5 flex items-center gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400">
              {t('review.notesHeading')}
            </span>
            <span className="text-[11px] text-surface-400">{sessionItems.length}</span>
          </div>
          {sessionItems.length === 0 ? (
            <p className="rounded-md border border-dashed border-surface-300 p-3 text-[13px] leading-relaxed text-surface-500">
              {t('review.noNotesBox', {
                name: displayName, month: formatMonthKey(month),
                sessions: row.sessions, present: row.present, absent: row.absent,
              })}
            </p>
          ) : (
            <ul className="flex flex-col gap-3 text-[13px] leading-[1.45] text-surface-700">
              {sessionItems.map((i) => (
                <li key={i.sessionId} className="flex gap-3">
                  <span
                    className={cn(
                      'w-[44px] shrink-0 tabular-nums text-[12px]',
                      i.attendance === 'ABSENT' ? 'text-destructive' : 'text-surface-500',
                    )}
                  >
                    {weekdayLabel(i.startAt.slice(0, 10))} {i.startAt.slice(8, 10)}
                  </span>
                  <span className="min-w-0 flex-1">
                    {i.attendance === 'ABSENT' ? (
                      <span className="text-surface-400">{t('review.absentDay')}</span>
                    ) : myText(i) ? (
                      <>
                        <span className="whitespace-pre-line">{myText(i)}</span>
                        {classText(i) && (
                          <span className="mt-0.5 block whitespace-pre-line text-[12px] text-surface-400">
                            <span className="text-surface-400">{t('review.classPrefix')}</span> {classText(i)}
                          </span>
                        )}
                      </>
                    ) : classText(i) ? (
                      <span className="whitespace-pre-line text-surface-500">
                        <span className="text-[12px] text-surface-400">{t('review.classPrefix')}</span> {classText(i)}
                      </span>
                    ) : null}
                    {/* D2 (handoff 21 §3, task 8): a muted photo count only —
                        no thumbnails, no lightbox, and the AI draft never
                        reads `homeworkPhotos` (it stays out of `myText`/
                        `classText` above). */}
                    {i.homeworkPhotos.length > 0 && (
                      <span className="text-surface-400"> · {t('notes.photos.count', { n: i.homeworkPhotos.length })}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex min-w-0 flex-col overflow-y-auto px-6 py-4">
          {/* Phone (mock frame 3): eyebrow on its own line, the AI button
              (with its lock-reason line) stacked directly beneath — a
              horizontal row here was fix round 1 finding 1, the eyebrow and
              button squeezed onto one line at 390px with the reason spilling
              to the right of it. `@[720px]:` restores Task 5's approved
              desktop row exactly. */}
          {/* Desktop: the eyebrow sits on the button's top line, not centred
              against the button + its lock-reason line (Johnny, 2026-09-04). */}
          <div className="mb-2.5 flex flex-col items-start gap-2 @[720px]:flex-row @[720px]:items-start @[720px]:gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400 @[720px]:pt-2">
              {t('review.title', { month: formatMonthKey(month) })}
            </span>
            <span className="hidden @[720px]:block @[720px]:flex-1" />
            <AiDraftButton
              state={aiState}
              busy={draft.isPending}
              noteCount={row.noteCount}
              onClick={onAiClick}
            />
          </div>

          {isDraft && draftSource !== 'ai' && (
            <p className="mb-2.5 rounded-sm bg-attention-tint/50 px-3.5 py-2.5 text-[12.5px] leading-[1.45] text-attention-foreground">
              {t('review.draftNotice')}
            </p>
          )}
          {draftSource === 'ai' && (
            <div className="mb-2.5 flex items-start gap-2.5 rounded-sm bg-brand-50 px-3.5 py-2.5">
              <Sparkles className="mt-0.5 h-[14px] w-[14px] shrink-0 text-brand-600" strokeWidth={2} />
              <p className="text-[12.5px] leading-[1.45] text-brand-700">
                {t('review.ai.banner', { count: draftCount, month: formatMonthKey(month) })}{' '}
                <button type="button" className="font-semibold underline" onClick={runDraft}>
                  {t('review.ai.redo')}
                </button>
                {' · '}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={() => { setContent(''); setDraftSource('manual'); }}
                >
                  {t('review.ai.clear')}
                </button>
              </p>
            </div>
          )}

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={draft.isPending}
            placeholder={t('review.editorPh', { name: displayName })}
            className="min-h-[220px] resize-none text-[15px] md:min-h-[200px] md:resize-y md:text-sm"
          />
          <p className="mt-2 text-[12px] leading-snug text-surface-400">
            {t('review.editorHint', { nextMonth: nextMonthLabel(month) })}
          </p>

          {/* Desktop: a shared footer bar owned by the panel root (below),
              outside this scrollable column. Phone: `SelectionBar` (its own
              `useClaimBottomBar` call, per its docstring) replaces the tab
              bar while writing, same as the attendance screen's own save
              footer (port guide §3.5) — a plain inline row here would sit
              BEHIND the iOS keyboard instead of floating above it. `stretch`
              is what lets "Lưu và sang học viên tiếp" actually fill the bar
              next to the fixed-width "Bỏ qua" (fix round 1 finding 2) —
              `SelectionBar` is portalled to `document.body`, so its position
              in this tree is irrelevant to where it renders. */}
          {isMobile && (
            <SelectionBar
              id="monthly-review-editor"
              ariaLabel={t('review.title', { month: formatMonthKey(month) })}
              active
              stretch
              actions={
                <>
                  {onSkip && (
                    <Button variant="outline" className="h-[44px] px-5" disabled={save.isPending} onClick={() => requestLeave(onSkip)}>
                      {t('review.skip')}
                    </Button>
                  )}
                  {isDraft && (
                    <Button variant="outline" className="h-[44px] px-4" disabled={save.isPending || !content.trim()} onClick={() => doSave(onSaved, 'DRAFT')}>
                      {t('review.saveDraft')}
                    </Button>
                  )}
                  <Button
                    className="h-[44px] flex-1 gap-2.5"
                    disabled={save.isPending || !content.trim()}
                    onClick={() => doSave(onSaveAndNext)}
                  >
                    {/* Single mode: `onSaveAndNext` IS `onSaved` (same close
                        callback), so only the LABEL needs to stop promising
                        a next student that doesn't exist on this entry
                        point. */}
                    {mode === 'sweep' ? (
                      <>
                        {isDraft ? t('review.approveNextShort') : t('review.saveNext')}
                        <ArrowRight className="h-4 w-4" strokeWidth={2} />
                      </>
                    ) : (
                      isDraft ? t('review.approve') : t('review.save')
                    )}
                  </Button>
                </>
              }
            />
          )}

          {/* A JSX descendant, never a sibling (INVARIANTS §6). */}
          <AlertDialog open={replaceOpen} onOpenChange={setReplaceOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('review.ai.replaceTitle')}</AlertDialogTitle>
                {/* No dedicated copy for this dialog beyond the title — the
                    warning is the same one the discard-confirm below uses
                    ("unsaved content will be lost"), which is exactly what
                    happens to whatever's in the box today. */}
                <AlertDialogDescription>{t('notes.discardBody')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('review.ai.replaceCancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => { setReplaceOpen(false); runDraft(); }}>
                  {t('review.ai.replaceConfirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Guards every exit this instance can be asked to make while
              dirty: picking another roster row, the page header's back
              button (both routed here via `guardLeave` on the ref), and the
              in-editor "Bỏ qua" button above (`requestLeave` is the same
              function either way) — mirrors the D1 discard dialog
              (SessionNotesPanel's `guardClose`/`confirmDiscard`). */}
          <AlertDialog open={pendingLeave !== null} onOpenChange={(o) => { if (!o) setPendingLeave(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('notes.discardTitle')}</AlertDialogTitle>
                <AlertDialogDescription>{t('notes.discardBody')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('notes.discardCancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    const proceed = pendingLeave;
                    setPendingLeave(null);
                    proceed?.();
                  }}
                >
                  {t('notes.discardConfirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {!isMobile && (
        <div className="flex shrink-0 items-center gap-2.5 border-t border-surface-100 bg-surface-50 px-6 py-4">
          {creditsLine && <span className="text-[12px] text-surface-400">{creditsLine}</span>}
          {isDraft ? (
            <>
              {/* THI-348: on a draft, the quiet button keeps it a draft and
                  the loud one approves it (publishes to the parent). */}
              <Button variant="outline" className="ml-auto h-[33.6px]" onClick={() => doSave(onSaved, 'DRAFT')} disabled={save.isPending || !content.trim()}>
                {t('review.saveDraft')}
              </Button>
              <Button className="h-[33.6px] gap-2 px-4.5" onClick={() => doSave(mode === 'sweep' ? onSaveAndNext : onSaved)} disabled={save.isPending || !content.trim()}>
                {mode === 'sweep' ? t('review.approveNext') : t('review.approve')}
                {mode === 'sweep' && <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2} />}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="ml-auto h-[33.6px]" onClick={() => doSave(onSaved)} disabled={save.isPending || !content.trim()}>
                {t('review.save')}
              </Button>
              {mode === 'sweep' && (
                <Button className="h-[33.6px] gap-2 px-4.5" onClick={() => doSave(onSaveAndNext)} disabled={save.isPending || !content.trim()}>
                  {t('review.saveNext')}
                  <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2} />
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});
